import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import {
  Card,
  Stack,
  Button,
  Popover,
  MenuItem,
  Container,
  Typography,
  CircularProgress,
  Box,
  Tab,
  Tabs,
} from "@mui/material";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import ActivePrizeDrawTable from "src/components/prize-draw-admin/ActivePrizeDrawTable";
import DefaultDetailDialog from "src/components/prize-draw-admin/DefaultDetail";
import DraftDrawTable from "src/components/prize-draw-admin/DraftDrawTable";
import ArchivedDrawTable from "src/components/prize-draw-admin/ArchivedDrawTable";
import { usePrizeDraws } from "src/hooks/api/usePrizeDraws";
import { useInboxMessages } from "src/hooks/api/useInboxMessages";

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 1, paddingTop: 3 }}>{children}</Box>}
    </div>
  );
}
const dateFormat = "YYYY-MM-DD HH:mm:ss";
export default function PrizeDrawsPage() {
  const navigate = useNavigate();
  const { 
    loading, 
    fetchPrizeDraws, 
    deletePrizeDraw, 
    deletePrizeDrawEntries,
    exportPrizeDrawCsv,
    refundTickets 
  } = usePrizeDraws();
  const { createInboxMessage } = useInboxMessages();
  
  const [open, setOpen] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rows, setRows] = useState(null);
  const [openPrizeDrawDialog, setOpenPrizeDrawDialog] = useState(false);
  const [prizeDraw, setPrizeDraw] = useState(null);
  const [value, setValue] = useState(0);
  const [prizeDrawList, setPrizeDrawList] = useState([]);
  const [userIds, setUserIds] = useState("");
  const [prizeDrawEntryData, setPrizeDrawEntryData] = useState([]);

  useEffect(() => {
    const loadPrizeDraws = async () => {
      try {
        const draws = await fetchPrizeDraws(1000, 0);
        const updatedRows = draws.filter(Boolean);
        const updated = updatedRows.filter((row) => {
          if (!row) return false;
          return row.status === 1;
        });
        setPrizeDrawList(updated);
        setRows(updatedRows);
      } catch (error) {
        console.error("Failed to load prize draws:", error);
      }
    };
    loadPrizeDraws();
  }, [fetchPrizeDraws]);
  const handleOpenMenu = (event, data) => {
    setPrizeDraw(data);
    setOpen(event.currentTarget);
    setOpenId(event.currentTarget.id);
  };
  const handleCloseMenu = () => {
    setOpen(null);
    setOpenId(null);
  };
  const handleOpenEditStageDialog = (raffleId) => {
    setOpen(null);
    setOpenId(null);
    navigate(`/dashboard/prize-draws/add`, {
      state: { prizeDraw, raffleId },
    });
  };
  
  const handleChangeTabs = (event, newValue) => {
    setValue(newValue);
    if (+newValue === 0) {
      const updated = rows?.filter((row) => {
        if (!row) return false;
        return row.status === 1;
      });
      setPrizeDrawList(updated);
    }
    if (+newValue === 1) {
      const updated = rows.filter((row) => {
        if (!row) return false;
        return row.status === 2
      });
      setPrizeDrawList(updated);
    } else if (+newValue === 2) {
      const updated = rows.filter((row) => {
        return row.status === 0;
      });
      setPrizeDrawList(updated);
    }
  };
  
  const handleExportCsv = async (raffleId) => {
    try {
      const response = await exportPrizeDrawCsv(raffleId);
      const downloadUrl = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", `${prizeDraw?.title}-${raffleId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to export CSV:", error);
    }
  };

  const handleSeeParticipants = (raffleId) => {
    navigate(`/dashboard/prize-draws/${raffleId}/participants`, {
      state: { prizes: prizeDraw, raffleId },
    });
  };

  return (
    <>
      <Helmet>
        <title> Prize Draws </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" gutterBottom>
            Prize Draws
          </Typography>
          <Stack spacing={2} direction="row">
            <Button
              variant="outlined"
              onClick={() => setOpenPrizeDrawDialog(true)}
              startIcon={<Iconify icon="eva:file-text-outline" />}
            >
              Default Details
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-outline" />}
              onClick={() => navigate("/dashboard/prize-draws/add")}
            >
              Create Prize Draw
            </Button>
          </Stack>
        </Stack>

        <Card>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={value}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
            >
              <Tab label="Active Prize Draws" {...a11yProps(0)} />
              <Tab label="Archived" {...a11yProps(1)} />
              <Tab label="Draft" {...a11yProps(2)} />
            </Tabs>
          </Box>
          <TabPanel value={value} index={0}>
            {rows === null || loading ? (
              <Box
                sx={{ display: "flex", justifyContent: "center" }}
                style={{ padding: 20 }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Scrollbar>
                <ActivePrizeDrawTable
                  rows={prizeDrawList}
                  handleOpenMenu={handleOpenMenu}
                />
              </Scrollbar>
            )}
          </TabPanel>
          <TabPanel value={value} index={1}>
            <Scrollbar>
              <ArchivedDrawTable
                rows={prizeDrawList}
                handleOpenMenu={handleOpenMenu}
              />
            </Scrollbar>
          </TabPanel>
          <TabPanel value={value} index={2}>
            <Scrollbar>
              <DraftDrawTable
                rows={prizeDrawList}
                handleOpenMenu={handleOpenMenu}
              />
            </Scrollbar>
          </TabPanel>
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleOpenEditStageDialog(openId)}>
            Edit
          </MenuItem>
        <MenuItem
          onClick={() => {
            handleSeeParticipants(openId);
          }}
        >
          See Tickets
        </MenuItem>
        <MenuItem onClick={() => handleExportCsv(openId)}>Export List</MenuItem>
      </Popover>

      {openPrizeDrawDialog && (
        <DefaultDetailDialog
          open={openPrizeDrawDialog}
          handleClose={() => setOpenPrizeDrawDialog(false)}
        />
      )}
    </>
  );
}
